/**
 * Shared allocation status helpers for list / view / edit UIs.
 */

export function normalizeAllocationStatusKey(status) {
  return String(status || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
}

export function displayAllocationStatus(status) {
  const raw = String(status || "").trim();
  if (!raw) return "";
  const key = normalizeAllocationStatusKey(raw);
  const MAP = {
    RESERVED: "Reserved",
    ALLOCATED: "Allocated",
    ELIGIBLE: "Eligible",
    PARTIALLY_ALLOCATED: "Partially Allocated",
    PARTIAL: "Partially Allocated",
    SHORTFALL: "Shortfall",
    PARTIALLY_DELIVERED: "Partially Delivered",
    PENDING_APPROVAL: "Pending Approval",
    BLOCKED: "Blocked",
    BLOCKED_BY_PRICE: "Blocked",
    DELIVERED: "Delivered",
    REVOKED: "Revoked",
  };
  if (MAP[key]) return MAP[key];
  if (key.includes("_")) {
    return key
      .toLowerCase()
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }
  return raw;
}

export function isClosedAllocationStatus(status) {
  const key = normalizeAllocationStatusKey(status);
  return key === "DELIVERED" || key === "REVOKED";
}

export function isDeliveredAllocationStatus(status, deliveryDate) {
  if (deliveryDate) return true;
  return isClosedAllocationStatus(status);
}

export function isPendingApprovalStatus(status) {
  const key = normalizeAllocationStatusKey(status);
  return key === "PENDING_APPROVAL";
}

/** Header statuses that still allow deliver / revoke actions. */
export function canActOnAllocationStatus(status) {
  return !isClosedAllocationStatus(status);
}

export function isPartiallyAllocatedHeaderStatus(status) {
  const key = normalizeAllocationStatusKey(status);
  // Service maps header SHORTFALL → Partially Allocated; accept both.
  return (
    key === "PARTIALLY_ALLOCATED" ||
    key === "PARTIAL" ||
    key === "SHORTFALL"
  );
}

/** Line statuses that must not appear in Mark as Delivered when head is partial. */
export function isUndeliverablePartialLineStatus(status) {
  const key = normalizeAllocationStatusKey(status);
  return (
    key === "BLOCKED" ||
    key === "BLOCKED_BY_PRICE" ||
    key === "SHORTFALL"
  );
}

export const ALLOCATION_STATUS_I18N = {
  Reserved: "status.reserved",
  Allocated: "inventory.statusAllocated",
  Eligible: "inventory.statusEligible",
  "Partially Allocated": "inventory.statusPartiallyAllocated",
  "Partially Delivered": "inventory.statusPartiallyDelivered",
  "Pending Approval": "inventory.statusPendingApproval",
  Shortfall: "inventory.statusShortfall",
  Blocked: "inventory.blockedByPriceLabel",
  Delivered: "inventory.statusDelivered",
  Revoked: "inventory.statusRevoked",
  Partial: "inventory.statusPartial",
};

export const ALLOCATION_STATUS_CLASS = {
  Reserved: "bg-amber-50 text-amber-700 border-amber-200",
  Allocated: "bg-sky-50 text-sky-700 border-sky-200",
  Eligible: "bg-teal-50 text-teal-700 border-teal-200",
  "Partially Allocated": "bg-orange-50 text-orange-700 border-orange-200",
  "Partially Delivered": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Pending Approval": "bg-violet-50 text-violet-700 border-violet-200",
  Shortfall: "bg-rose-50 text-rose-700 border-rose-200",
  Blocked: "bg-rose-50 text-rose-700 border-rose-200",
  Delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Revoked: "bg-rose-50 text-rose-700 border-rose-200",
  Partial: "bg-orange-50 text-orange-700 border-orange-200",
};

export function allocationStatusLabel(status, t) {
  const display = displayAllocationStatus(status);
  const key = ALLOCATION_STATUS_I18N[display];
  return key ? t(key) : display || "—";
}

export function allocationStatusClass(status) {
  const display = displayAllocationStatus(status);
  return (
    ALLOCATION_STATUS_CLASS[display] ||
    "bg-gray-50 text-gray-600 border-gray-200"
  );
}
